import { useEffect, useRef, useState } from 'react';
import { Device } from '@twilio/voice-sdk';

// #1419 v2b Stufe 1 — minimale Softphone-Testseite (KEIN finales Widget).
// Registriert sich mit Identity ma_<userId>, nimmt eingehende Calls an.
export default function SoftphoneTest() {
  const [status, setStatus] = useState('idle'); // idle|connecting|ready|incoming|in-call|error
  const [identity, setIdentity] = useState('');
  const [log, setLog] = useState([]);
  const [testCallBusy, setTestCallBusy] = useState(false);
  const deviceRef = useRef(null);
  const callRef = useRef(null);

  const addLog = (msg) => setLog((l) => [...l.slice(-30), `${new Date().toLocaleTimeString('de-AT')} — ${msg}`]);

  const fetchToken = async () => {
    const res = await fetch('/api/twilio/voice/token', { credentials: 'include' });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
    return res.json();
  };

  const connect = async () => {
    try {
      setStatus('connecting');
      addLog('Hole Access Token …');
      const { token, identity: id } = await fetchToken();
      setIdentity(id);
      const device = new Device(token, { logLevel: 'error', codecPreferences: ['opus', 'pcmu'] });
      deviceRef.current = device;

      device.on('registered', () => { setStatus('ready'); addLog(`Registriert als ${id}`); });
      device.on('error', (e) => { addLog(`Fehler: ${e.message}`); setStatus('error'); });
      device.on('unregistered', () => addLog('Abgemeldet'));
      device.on('tokenWillExpire', async () => {
        try { device.updateToken((await fetchToken()).token); addLog('Token erneuert'); }
        catch (e) { addLog(`Token-Erneuerung fehlgeschlagen: ${e.message}`); }
      });
      device.on('incoming', (call) => {
        callRef.current = call;
        setStatus('incoming');
        addLog(`Eingehender Anruf von ${call.parameters.From || '?'}`);
        call.on('disconnect', () => { callRef.current = null; setStatus('ready'); addLog('Anruf beendet'); });
        call.on('cancel', () => { callRef.current = null; setStatus('ready'); addLog('Anruf abgebrochen'); });
      });

      await device.register();
    } catch (e) {
      addLog(`Verbindung fehlgeschlagen: ${e.message}`);
      setStatus('error');
    }
  };

  const accept = () => { callRef.current?.accept(); setStatus('in-call'); addLog('Angenommen'); };
  const hangup = () => { callRef.current?.disconnect(); };
  const reject = () => { callRef.current?.reject(); callRef.current = null; setStatus('ready'); addLog('Abgelehnt'); };

  const triggerTestCall = async () => {
    setTestCallBusy(true);
    try {
      const res = await fetch('/api/twilio/voice/test-call', { method: 'POST', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      addLog(`Test-Call ausgelöst (${data.call_sid}) — Browser sollte gleich klingeln`);
    } catch (e) {
      addLog(`Test-Call fehlgeschlagen: ${e.message}`);
    } finally {
      setTestCallBusy(false);
    }
  };

  useEffect(() => () => { deviceRef.current?.destroy(); }, []);

  const badge = {
    idle: ['Nicht verbunden', '#6B7280'],
    connecting: ['Verbinde …', '#6B7280'],
    ready: ['Bereit (registriert)', '#16A34A'],
    incoming: ['📞 Eingehender Anruf', '#338edc'],
    'in-call': ['Im Gespräch', '#16A34A'],
    error: ['Fehler', '#DC2626'],
  }[status];

  return (
    <div className="p-6 max-w-2xl">
      <h1 className="text-xl font-semibold mb-1" style={{ color: 'var(--text-primary, #1A1A2E)' }}>Softphone-Test (#1419)</h1>
      <p className="text-sm mb-4" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
        v2b Stufe 1 — WebRTC-Gerüst. Verbinden → Test-Anruf auslösen → annehmen.
      </p>

      <div className="border rounded-lg p-4 mb-4" style={{ borderColor: '#E5E7EB' }}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium" style={{ color: badge[1] }}>{badge[0]}</span>
          {identity && <span className="text-xs font-mono" style={{ color: 'var(--text-muted, #9CA3AF)' }}>{identity}</span>}
        </div>
        <div className="flex gap-2 flex-wrap">
          {(status === 'idle' || status === 'error') && (
            <button onClick={connect} className="px-4 py-2 rounded-lg text-white text-sm" style={{ background: '#338edc' }}>Verbinden</button>
          )}
          {status === 'ready' && (
            <button onClick={triggerTestCall} disabled={testCallBusy} className="px-4 py-2 rounded-lg text-white text-sm disabled:opacity-50" style={{ background: '#338edc' }}>
              {testCallBusy ? 'Löse aus …' : 'Test-Anruf auslösen'}
            </button>
          )}
          {status === 'incoming' && (
            <>
              <button onClick={accept} className="px-4 py-2 rounded-lg text-white text-sm" style={{ background: '#16A34A' }}>Annehmen</button>
              <button onClick={reject} className="px-4 py-2 rounded-lg text-white text-sm" style={{ background: '#DC2626' }}>Ablehnen</button>
            </>
          )}
          {status === 'in-call' && (
            <button onClick={hangup} className="px-4 py-2 rounded-lg text-white text-sm" style={{ background: '#DC2626' }}>Auflegen</button>
          )}
        </div>
      </div>

      <div className="border rounded-lg p-4" style={{ borderColor: '#E5E7EB' }}>
        <div className="text-xs font-medium mb-2" style={{ color: 'var(--text-secondary, #4B5563)' }}>Log</div>
        <div className="text-xs font-mono space-y-1" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
          {log.length === 0 ? <div>—</div> : log.map((l, i) => <div key={`${i}-${l}`}>{l}</div>)}
        </div>
      </div>
    </div>
  );
}
